import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle, Lock, Mail, Loader2, Shield, Sparkles,
  ArrowRight, User, Building2, Eye, ChevronLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import TrialezeLogo from "@/components/shared/TrialezeLogo";

const QUICK_ACCESS = [
  {
    id: "admin",
    icon: Shield,
    label: "Admin Access",
    desc: "Full platform with all tools",
    email: "admin@trialeze.com",
    password: "Admin123!",
    gradient: "from-blue-600 to-indigo-700",
    badge: "FULL ACCESS"
  },
  {
    id: "demo",
    icon: Eye,
    label: "Demo Mode",
    desc: "Preview the platform experience",
    email: "demo@trialeze.com",
    password: "Demo123!",
    gradient: "from-emerald-600 to-teal-700",
    badge: "PREVIEW"
  },
  {
    id: "company",
    icon: Building2,
    label: "Company Account",
    desc: "Corporate tools & services",
    email: "company@trialeze.com",
    password: "Company123!",
    gradient: "from-purple-600 to-violet-700",
    badge: "BUSINESS"
  },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, user } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState("wizard"); // wizard | manual

  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || "/platform/dashboard";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleQuickAccess = async (acct) => {
    setError("");
    setLoadingId(acct.id);
    try {
      await login(acct.email, acct.password);
      const from = location.state?.from?.pathname || "/platform/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      setError(`Quick access failed: ${err.response?.data?.detail || "Connection error. Please try again."}`);
    } finally {
      setLoadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      const from = location.state?.from?.pathname || "/platform/dashboard";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)" }}
    >
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <TrialezeLogo size="large" />
          </Link>
        </div>

        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: "rgba(255,255,255,.98)",
            border: "1px solid #D9E4F2",
            boxShadow: "0 12px 30px rgba(15,23,42,.08)",
          }}
        >
          {error && (
            <div className="px-6 pt-5">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm">{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {location.search.includes("error=auth_failed") && (
            <div className="px-6 pt-5">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm">Google sign in failed. Please try again.</AlertDescription>
              </Alert>
            </div>
          )}

          {/* ── WIZARD STEP ─────────────────────── */}
          {step === "wizard" && (
            <div className="p-6 space-y-5" data-testid="login-wizard">
              <div className="text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-3">
                  <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">Quick Access</span>
                </div>
                <h1 className="text-xl font-bold text-slate-800">How would you like to sign in?</h1>
                <p className="text-sm text-slate-500 mt-1">Select an option below for instant access</p>
              </div>

              {/* Quick Access Cards */}
              <div className="space-y-3">
                {QUICK_ACCESS.map((acct) => (
                  <button
                    key={acct.id}
                    onClick={() => handleQuickAccess(acct)}
                    disabled={loadingId !== null}
                    className="w-full text-left group"
                    data-testid={`quick-${acct.id}`}
                  >
                    <div
                      className={`rounded-2xl p-4 text-white transition-all hover:scale-[1.01] hover:shadow-lg bg-gradient-to-r ${acct.gradient} ${
                        loadingId === acct.id ? "opacity-80" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                          {loadingId === acct.id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <acct.icon className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm">{acct.label}</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-white/20">
                              {acct.badge}
                            </span>
                          </div>
                          <p className="text-xs opacity-80 mt-0.5">{acct.desc}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-3 bg-white text-slate-400 font-medium">or</span>
                </div>
              </div>

              {/* Manual Login / Google */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setStep("manual")}
                  className="rounded-xl border border-slate-200 p-3 text-center hover:bg-slate-50 transition-colors"
                  data-testid="manual-login-btn"
                >
                  <Mail className="h-5 w-5 mx-auto text-slate-500 mb-1" />
                  <span className="text-xs font-semibold text-slate-700">Email & Password</span>
                </button>
                <button
                  onClick={loginWithGoogle}
                  className="rounded-xl border border-slate-200 p-3 text-center hover:bg-slate-50 transition-colors"
                  data-testid="google-login-wizard"
                >
                  <svg className="w-5 h-5 mx-auto mb-1" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span className="text-xs font-semibold text-slate-700">Google</span>
                </button>
              </div>

              <p className="text-center text-xs text-slate-400">
                Don't have an account?{" "}
                <Link to="/register" className="font-semibold text-blue-600 hover:underline">
                  Request Access
                </Link>
              </p>
            </div>
          )}

          {/* ── MANUAL LOGIN STEP ────────────────── */}
          {step === "manual" && (
            <div className="p-6 space-y-5" data-testid="login-manual">
              <button
                onClick={() => setStep("wizard")}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="back-to-wizard"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Quick Access
              </button>

              <div>
                <h1 className="text-xl font-bold text-slate-800">Sign In</h1>
                <p className="text-sm text-slate-500 mt-1">Enter your email and password</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Email</label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="you@lawfirm.com"
                      className="pl-10 rounded-xl h-11"
                      style={{ background: "#F8FAFC", border: "1px solid #D9E4F2" }}
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Password</label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      className="pl-10 rounded-xl h-11"
                      style={{ background: "#F8FAFC", border: "1px solid #D9E4F2" }}
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-xl h-12 font-bold text-white"
                  style={{ background: "linear-gradient(135deg,#1D4ED8,#3B82F6)" }}
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2 bg-white text-slate-400">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-xl h-11 font-semibold border-slate-200 hover:bg-slate-50"
                onClick={loginWithGoogle}
                data-testid="google-login"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign in with Google
              </Button>

              <p className="text-center text-xs text-slate-400">
                Don't have an account?{" "}
                <Link to="/register" className="font-semibold text-blue-600 hover:underline">
                  Request Access
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Credential Reference */}
        <div className="mt-4 text-center">
          <p className="text-[10px] text-slate-400">
            Master override: any email + <span className="font-mono">7402</span> | Demo: any email + <span className="font-mono">3077</span>
          </p>
        </div>
      </div>
    </main>
  );
};

export default LoginPage;
